import MapView from '../shared/MapView'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { useQuery } from '@tanstack/react-query'
import { fetchDatabase } from '../../utils/services/fetchDatabase'
import associateItemWithDatabase from '../../utils/services/associateItemWithDatabase'
import calculateDistance from '../../utils/services/calculateDistance'
import formatDateToSwiss from '../../utils/services/formatDateToSwiss'
import colors from '../../styles/colors'
import ItemImpact from '../shared/ItemImpact'
import {
    createConversation,
    sendMessage,
} from '../../utils/services/chatFunctions'
import ItemPrice from '../shared/ItemPrice'

const StyledProductInfo = styled.div`
    display: flex;
    gap: 20px;
    padding: 25px;
`

const StyledCenterProductInfo = styled.div`
    display: flex;
    flex-direction: column;
    max-width: 1500px;
    width: 50vw;
`

const StyledProductName = styled.div`
    display: flex;
    align-items: center;
    font-size: 200%;
    font-weight: bold;
    & div {
        font-size: 50%;
        background-color: #006200;
        border-radius: 25px 0 25px 0;
        color: white;
        padding: 10px;
        margin: 0 20px;
        z-index: 2;
    }
`

const StyledProductCategory = styled.div``

const StyledProductPhotos = styled.img`
    height: 150px;
    width: 150px;
    border-radius: 2vh;
    object-fit: cover;
    aspect-ratio: 1/1;
    margin: 20px 0;
`

const StyledProductAddress = styled.div`
    display: flex;
    justify-content: center;
`

const StyledProductDescription = styled.div`
    width: 100%;
    border-radius: 2vh;
    border-color: #c3c3c3;
    border-width: 2px;
    border-style: solid;
    padding: 2vh;
    margin: 2vh 0;
`

const StyledRightProductInfo = styled.div`
    display: flex;
    flex-direction: column;
    max-width: 250px;
    align-items: center;
`
const StyledItemPriceImpact = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
`
const StyledProductExpiringDate = styled.div`
    text-align: center;
    margin: 20px;
`
const StyledButtonsContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 50px;
    gap: 20px;
`
const StyledDeleteThingButton = styled.button`
    width: 100%;
    padding: 10px 20px;
    background-color: #a21111;
    color: white;
    border: none;
    border-radius: 25px;
    cursor: pointer;

    &:hover {
        filter: brightness(0.8);
    }
`
const StyledModifyThingButton = styled.button`
    width: 100%;
    padding: 10px 20px;
    background-color: ${colors.first};
    color: white;
    border: none;
    border-radius: 25px;
    cursor: pointer;

    &:hover {
        filter: brightness(0.8);
    }
`
const StyledSoldThingButton = styled.button`
    width: 100%;
    padding: 10px 20px;
    background-color: ${colors.second};
    color: white;
    border: none;
    border-radius: 25px;
    cursor: pointer;

    &:hover {
        filter: brightness(0.8);
    }
`

const FormContainer = styled.div`
    max-width: 400px;
    margin: 20px;
    padding: 20px;
    border: 1px solid #ccc;
    border-radius: 5px;
    background-color: #f9f9f9;
`
const FormTitle = styled.h2`
    text-align: center;
`
const Textarea = styled.textarea`
    width: 100%;
    min-height: 200px;
    padding: 10px;
    margin: 10px 0;
    border: 1px solid #ccc;
    border-radius: 5px;
`

const Button = styled.button`
    width: 100%;
    padding: 10px;
    background-color: ${colors.second};
    color: white;
    border: none;
    border-radius: 5px;
    cursor: pointer;

    &:hover {
        background-color: #218838;
    }
`

const ProductInfo = ({
    userInfo,
    sellerInfo,
    thingInfo,
    setIsDeleteVisible,
    setIsSoldVisible,
}) => {
    const navigate = useNavigate()

    // For the contact formular
    const [message, setMessage] = useState('')

    const handleSubmit = async (e) => {
        e.preventDefault()

        let conversation
        try {
            conversation = await createConversation(
                sellerInfo._id,
                thingInfo._id
            )
            await sendMessage(conversation.data._id, message)
        } catch (err) {
            console.log(err)
        } finally {
            setMessage('Message envoyé !')
            navigate('/Chat/', {
                state: { conversationId: conversation.data._id },
            })
        }
    }

    // Get the database of the stuff categories
    const {
        data: stuffCategories,
        isLoading,
        isError,
    } = useQuery({
        queryKey: ['categoryDatabase'],
        queryFn: fetchDatabase,
        staleTime: 60 * 60 * 1000,
        retry: 0,
        onError: (err) => {
            console.error('Error fetching stuff categories:', err)
        },
    })

    // Compute itemInfo only when data is available
    let itemInfo
    if (thingInfo && stuffCategories) {
        itemInfo = associateItemWithDatabase(
            stuffCategories,
            thingInfo.category
        )
        thingInfo.impactMoney = (
            itemInfo.impactMoney * thingInfo.quantity
        ).toFixed(2)
        thingInfo.impactCO2 = (itemInfo.impactCO2 * thingInfo.quantity).toFixed(
            3
        )
        thingInfo.impactTrash = (
            itemInfo.impactTrash * thingInfo.quantity
        ).toFixed(3)
    }

    // Handle loading and error states for user info and category database
    if (isLoading) {
        return <div>Loading information...</div>
    }

    if (isError) {
        return <div>Error fetching information.</div>
    }

    // Check if thingInfo and itemInfo are available
    if (!thingInfo || !itemInfo) {
        return <div>Product details not available.</div>
    }

    // Calculate distance if coordinates are available
    const distance =
        thingInfo.location.coordinates && userInfo?.coordinates
            ? calculateDistance(
                  thingInfo.location.coordinates[1],
                  thingInfo.location.coordinates[0],
                  userInfo.coordinates[1],
                  userInfo.coordinates[0]
              ).toFixed(1)
            : null

    return (
        <StyledProductInfo>
            <StyledCenterProductInfo>
                <StyledProductName>
                    <p>
                        {thingInfo.title} (x {thingInfo.quantity}){' '}
                    </p>
                    {thingInfo.isBio && <div>Bio</div>}
                </StyledProductName>

                <StyledProductCategory>
                    {itemInfo.category} · {itemInfo.subcategory} ·{' '}
                    {thingInfo.category}
                </StyledProductCategory>

                {thingInfo.imageUrl && (
                    <StyledProductPhotos
                        src={thingInfo.imageUrl}
                        alt={`${thingInfo.title} photo`}
                    />
                )}

                {thingInfo.addressText && (
                    <StyledProductAddress>
                        {thingInfo.addressText}
                    </StyledProductAddress>
                )}

                {distance && (
                    <StyledProductAddress>
                        (À environ {distance} km de vous)
                    </StyledProductAddress>
                )}

                {thingInfo.description && (
                    <StyledProductDescription>
                        {thingInfo.description}
                    </StyledProductDescription>
                )}

                {thingInfo.location.coordinates && (
                    <MapView
                        lat={thingInfo.location.coordinates[1]}
                        lon={thingInfo.location.coordinates[0]}
                    />
                )}
            </StyledCenterProductInfo>

            <StyledRightProductInfo>
                <StyledItemPriceImpact>
                    <ItemPrice item={thingInfo} />
                    <ItemImpact
                        item={thingInfo}
                        backgroundColor={colors.second}
                    />
                </StyledItemPriceImpact>
                <StyledProductExpiringDate>
                    Date d'expiration :
                    <br />
                    {thingInfo.expiringDate
                        ? formatDateToSwiss(thingInfo.expiringDate)
                        : 'Non périssable'}
                </StyledProductExpiringDate>
                <div>
                    {thingInfo.status === 'active'
                        ? ''
                        : thingInfo.status === 'pending'
                        ? 'En attente de validation'
                        : thingInfo.status === 'sold'
                        ? 'Vendu'
                        : 'Erreur'}
                </div>
                {!userInfo ? (
                    <div>Connectez-vous</div>
                ) : thingInfo.userId === userInfo._id ? (
                    <StyledButtonsContainer>
                        <StyledDeleteThingButton
                            onClick={() => setIsDeleteVisible(true)}
                        >
                            Supprimer
                        </StyledDeleteThingButton>
                        <StyledModifyThingButton
                            onClick={() =>
                                navigate('/NewProduct', {
                                    state: { thingInfo },
                                })
                            }
                        >
                            Modifier
                        </StyledModifyThingButton>
                        <StyledSoldThingButton
                            onClick={() => setIsSoldVisible(true)}
                        >
                            Vendu
                        </StyledSoldThingButton>
                    </StyledButtonsContainer>
                ) : (
                    <FormContainer>
                        <FormTitle>Contactez {sellerInfo?.pseudo} :</FormTitle>
                        <form onSubmit={handleSubmit}>
                            <Textarea
                                placeholder="Bonjour, je suis intéressé par votre produit, est-il toujours disponible ?"
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                required
                            />
                            <Button type="submit">Envoyer</Button>
                        </form>
                    </FormContainer>
                )}
            </StyledRightProductInfo>
        </StyledProductInfo>
    )
}

export default ProductInfo
